import { decodeHubInfo, HUB_FILE_NAMES, type HubFileId, type HubInfo } from '../ble/hubProtocol'
import {
  commitHubBlock, getHubFileMeta, resetHubFile, verifyHubFile,
  type HubFileMeta, type HubSessionRef,
} from '../db/offlineDb'

const BLOCK_SIZE = 64 * 1024
const REQUEST_TIMEOUT_MS = 30_000
const RETRIES = 3

export interface WifiHubFile {
  fileId: HubFileId
  name: string
  size: number
  crc32: number
  etag: string
}

export interface WifiHubManifest {
  wifiProtocolVersion: 1
  info: HubInfo
  files: WifiHubFile[]
}

type Fetcher = typeof fetch

export function normalizeHubUrl(input: string, pageUrl = window.location.href): string {
  const value = input.trim()
  if (!value) throw new Error('Укажите HTTPS-адрес хаба или шлюза')
  const page = new URL(pageUrl)
  const url = new URL(value, page)
  if (url.username || url.password || url.search || url.hash) throw new Error('Адрес хаба не должен содержать логин, пароль или параметры')
  if (url.protocol !== 'https:' && !(page.protocol === 'http:' && url.origin === page.origin)) {
    throw new Error('PWA требует HTTPS-хаб или шлюз того же origin; прямой HTTP по локальному IP блокируется браузером')
  }
  return url.href.replace(/\/$/, '')
}

export function parseWifiHubManifest(value: unknown): WifiHubManifest {
  if (!value || typeof value !== 'object') throw new Error('Некорректный манифест Wi-Fi-хаба')
  const raw = value as Record<string, unknown>
  if (raw.wifiProtocolVersion !== 1) throw new Error('Версия Wi-Fi-протокола хаба не поддерживается')
  const info = decodeHubInfo(JSON.stringify(raw.info))
  const expectedCount = info.sessionState === 'EMPTY' ? 0 : 5
  if (!Array.isArray(raw.files) || raw.files.length !== expectedCount) {
    throw new Error(`Хаб должен объявить ${expectedCount === 5 ? 'пять' : 'ноль'} файлов`)
  }
  const files = raw.files.map((entry, index) => {
    if (!entry || typeof entry !== 'object') throw new Error('Некорректное описание файла хаба')
    const file = entry as Record<string, unknown>
    if (file.fileId !== index + 1 || file.name !== HUB_FILE_NAMES[index]
      || !Number.isInteger(file.size) || (file.size as number) < 0 || (file.size as number) > 0xffffffff
      || !Number.isInteger(file.crc32) || (file.crc32 as number) < 0 || (file.crc32 as number) > 0xffffffff
      || typeof file.etag !== 'string' || !/^"[\x21\x23-\x7e]+"$/.test(file.etag)) {
      throw new Error(`Некорректная запись файла ${HUB_FILE_NAMES[index]}`)
    }
    return { fileId: file.fileId as HubFileId, name: file.name as string,
      size: file.size as number, crc32: file.crc32 as number, etag: file.etag as string }
  })
  const total = files.reduce((sum, file) => sum + file.size, 0)
  if (total !== info.totalBytes) throw new Error('Сумма размеров файлов не совпадает с INFO хаба')
  return { wifiProtocolVersion: 1, info, files }
}

export class WifiHubTransport {
  private readonly baseUrl: string
  private readonly fetcher: Fetcher
  private manifest?: WifiHubManifest

  constructor(baseUrl: string, fetcher: Fetcher = fetch) {
    this.baseUrl = normalizeHubUrl(baseUrl)
    this.fetcher = fetcher
  }

  async connect(): Promise<WifiHubManifest> {
    const manifest = await this.request('/api/v1/session', {}, async response => {
      if (!response.headers.get('Content-Type')?.toLowerCase().startsWith('application/json')) {
        throw new Error('Адрес не ведёт на API хаба: ожидается JSON-манифест. Проверьте HTTPS-шлюз /hub')
      }
      return parseWifiHubManifest(await response.json())
    })
    this.manifest = manifest
    return manifest
  }

  async readFiles(palletId: string, onProgress: (done: number, total: number, fileName: string) => void): Promise<{ ref: HubSessionRef; files: HubFileMeta[] }> {
    const manifest = this.manifest ?? await this.connect()
    const { info } = manifest
    if (info.sessionState !== 'READY' && info.sessionState !== 'ACKED') {
      throw new Error(`Сеанс хаба не готов к выгрузке: ${info.sessionState}`)
    }
    const ref: HubSessionRef = { palletId, serialNumber: info.serialNumber, sessionId: info.sessionId, sessionToken: info.sessionToken }
    const files: HubFileMeta[] = []
    let completed = 0
    for (const file of manifest.files) {
      let meta = await getHubFileMeta(ref, file.fileId)
      if (meta && (meta.fileSize !== file.size || meta.fileCrc32 !== file.crc32 || meta.fileName !== file.name)) {
        throw new Error(`Манифест ${file.name} отличается от локально сохранённого сеанса`)
      }
      if (meta && meta.committedOffset > file.size) throw new Error(`Локальное смещение ${file.name} больше размера файла`)
      let crcRestarted = false
      while (!meta?.verified) {
        const offset = meta?.committedOffset ?? 0
        if (offset === file.size) {
          try {
            meta = await verifyHubFile(ref, file.fileId)
          } catch (error) {
            if (crcRestarted) throw error
            await resetHubFile(ref, file.fileId)
            meta = undefined
            crcRestarted = true
          }
          continue
        }
        if (file.size === 0) {
          meta = await commitHubBlock(ref, file.fileId, file.name, 0, 0, file.crc32, new Uint8Array())
          continue
        }
        const end = Math.min(offset + BLOCK_SIZE, file.size) - 1
        const bytes = await this.readBlock(file, offset, end)
        meta = await commitHubBlock(ref, file.fileId, file.name, offset, file.size, file.crc32, bytes)
        onProgress(completed + meta.committedOffset, info.totalBytes, file.name)
      }
      files.push(meta)
      completed += file.size
      onProgress(completed, info.totalBytes, file.name)
    }
    return { ref, files }
  }

  async acknowledge(): Promise<void> {
    if (!this.manifest) throw new Error('Нет открытого сеанса хаба')
    const { info } = this.manifest
    const value = await this.request(`/api/v1/sessions/${encodeURIComponent(info.sessionId)}/ack`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: info.sessionToken }),
    }, response => response.json() as Promise<Record<string, unknown>>)
    if (value.sessionState !== 'ACKED' || value.sessionToken !== info.sessionToken) {
      throw new Error('Хаб не подтвердил ACK для текущего сеанса')
    }
  }

  private async readBlock(file: WifiHubFile, start: number, end: number): Promise<Uint8Array> {
    let lastError: unknown
    for (let attempt = 0; attempt < RETRIES; attempt++) {
      try {
        return await this.request(`/api/v1/files/${file.fileId}`, {
          headers: { Range: `bytes=${start}-${end}`, 'If-Match': file.etag },
        }, async response => {
          if (response.status !== 206 || response.headers.get('Content-Range') !== `bytes ${start}-${end}/${file.size}`
            || response.headers.get('ETag') !== file.etag) {
            throw new Error(`Хаб вернул неверный диапазон или ETag файла ${file.name}`)
          }
          const bytes = new Uint8Array(await response.arrayBuffer())
          if (bytes.length !== end - start + 1) throw new Error(`Неполный блок файла ${file.name}`)
          return bytes
        })
      } catch (error) {
        lastError = error
        if (error instanceof HubHttpError && !error.retryable) break
      }
    }
    throw lastError
  }

  private async request<T>(path: string, init: RequestInit, consume: (response: Response) => Promise<T>): Promise<T> {
    const controller = new AbortController()
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    try {
      const response = await this.fetcher(`${this.baseUrl}${path}`, {
        ...init, signal: controller.signal, cache: 'no-store', credentials: 'omit',
      })
      if (!response.ok) throw new HubHttpError(response.status)
      return await consume(response)
    } catch (error) {
      if (controller.signal.aborted) throw new Error('Хаб не ответил за 30 секунд')
      if (error instanceof TypeError) throw new Error('Нет связи с Wi-Fi-хабом. Проверьте сеть, HTTPS-сертификат и разрешения локальной сети браузера')
      throw error
    } finally {
      window.clearTimeout(timeout)
    }
  }
}

class HubHttpError extends Error {
  readonly retryable: boolean
  constructor(status: number) {
    super(`Хаб вернул HTTP ${status}${status === 412 ? ': сеанс или файл изменился' : ''}`)
    this.retryable = status === 429 || status >= 500
  }
}
